import Excel from 'exceljs';
import { ClaimRow } from '../../../api/endpoints';
import { formatDate, US_DATE_FORMAT } from '../../../utils/formatDate';
import { StructureFooter } from '../typesExhibitExporter';
import { getClaimNumber } from '../utils/field';
import { IExcelCreator } from './excelExporter';
import { ExhibitTemplateProps } from './exhibitType';
import {
  createTotalRowStyle,
  defCellStyle,
  defPositionNumberStyle,
} from './utils/excelConst';
import {
  createExcelWorkbook,
  ExcelDocumentHeader,
} from './utils/excelCreateWorkbook';
import { excelAddPageHeader, HeaderPositionCell } from './utils/excelHeader';
import {
  deleteObjectionCommentaryColumn,
  excelAddPageFooter,
  excelAddRow,
  excelAddTableHeaderRow,
  excelAddTotalRow,
} from './utils/excelUtils';
import {
  createClaimAmount,
  createHeaderFooter,
  createReducedClaimAmount,
  getAddressLine,
  getTotalCurrency,
} from './utils/exporterUtils';
import { IExhibitWorkbookData, IRowValues, TRowStyle } from './utils/type';

const COLUMN_LIST: ExcelDocumentHeader[] = [
  { header: '', width: 4 },
  { header: 'Original Creditor', width: 20 },
  { header: 'Claim to be modified', width: 12 },
  { header: 'Original Debtor', width: 19 },
  { header: 'Date filed/scheduled', width: 11 },
  { header: 'Unliquidated', width: 9 },
  { header: 'Claim amount and priority (1)', width: 13 },
  { header: 'Modified Claim amount and/or priority (1)', width: 13 },
  { header: 'Basis for objection', width: 15 },
  { header: 'Comments', width: 15 },
];

const valuesStyle: TRowStyle = [
  { ...defPositionNumberStyle },
  { ...defCellStyle },
  {
    ...defCellStyle,
    alignment: { horizontal: 'center', vertical: 'top' },
  },
  { ...defCellStyle },
  { ...defCellStyle },
  { ...defCellStyle },
  {
    ...defCellStyle,
    numFmt: '[$$-409]#,##0.00',
    alignment: { horizontal: 'right', vertical: 'top', wrapText: true },
  },
  {
    ...defCellStyle,
    numFmt: '[$$-409]#,##0.00',
    alignment: { horizontal: 'right', vertical: 'top', wrapText: true },
  },
  { ...defCellStyle },
  { ...defCellStyle },
];

const valuesTotalStyle = createTotalRowStyle(valuesStyle.length);

const objectionColId = 10;

const PAGE_HEADER_POS: HeaderPositionCell = {
  left: 1,
  middle: 4,
  right: 7,
  length: 10,
};

export const templateAlterClaimAmount: IExcelCreator = {
  fileName: 'Alter_ClaimAmount.xlsx',
  getWorkbook: async (props: ExhibitTemplateProps) => {
    const { displayObjection } = props.exhibit.structure;
    const excelData = getDataToShow(props);
    return await createWorkbook(excelData, displayObjection);
  },
};

async function createWorkbook(
  data: IExhibitWorkbookData,
  displayObjection: boolean | undefined,
): Promise<Excel.Workbook> {
  const workbook = createExcelWorkbook('Sheet1', COLUMN_LIST);
  const firstSheet = workbook.getWorksheet(1);

  excelAddPageHeader(firstSheet, data.headerFooter.header, PAGE_HEADER_POS);
  excelAddTableHeaderRow(firstSheet, COLUMN_LIST);
  excelAddPageFooter(firstSheet, data.headerFooter);

  data.claimsList.forEach((row) => excelAddRow(firstSheet, row, valuesStyle));
  excelAddTotalRow(firstSheet, data.totalRow, valuesTotalStyle);

  deleteObjectionCommentaryColumn(firstSheet, displayObjection, objectionColId);
  return workbook;
}

export function getDataToShow(props: ExhibitTemplateProps): IExhibitWorkbookData {
  const { exhibit, claimItems } = props;
  const { footer } = exhibit.structure;

  const showClaimCount = footer.includes(StructureFooter.ClaimCount);

  return {
    headerFooter: createHeaderFooter(props),
    claimsList: getPreparedClaimsData(
      claimItems,
      exhibit.structure.displayObjection,
      exhibit.objectionBasis,
    ),
    totalRow: getTotalRow(claimItems, showClaimCount),
  };
}

function getTotalRow(claims: ClaimRow[], showClaimCount: boolean): IRowValues {
  const values = [
    '', //positionNumber
    'Claims to be modified totals', //originalCreditor
    showClaimCount ? `Count: ${claims.length}` : '', //referenceNumber
    '', //originalDebtor
    '', //officialClaimDate
    '', //unliquidated
    getTotalCurrency(claims, 'currentTotal'), //claimAmount
    getTotalCurrency(claims, 'proposedTotal'), //reducedClaimAmount
    '', //objectionBasis
  ];

  return { values };
}

const getPreparedClaimsData = (
  claims: ClaimRow[],
  displayObjection: boolean | undefined,
  objectionBasis: string,
) => {
  return claims.map(
    (c, index) =>
      ({
        values: [
          index + 1, //positionNumber
          getAddressLine(c, c.redactNameAddress), //originalCreditor
          getClaimNumber(c), //referenceNumber
          c.originalDebtor, //originalDebtor
          formatDate(c.officialClaimDate, US_DATE_FORMAT), //officialClaimDate
          getUnliquidated(c.originalUnliquidated), //unliquidated
          createClaimAmount(c), //claimAmount
          createReducedClaimAmount(c), //reducedClaimAmount
          objectionBasis, //objectionBasis
          displayObjection ? c.objectionCommentary : '', //objectionCommentary
        ],
      } as IRowValues),
  );
};

const getUnliquidated = (checked: boolean | undefined) => {
  return checked ? 'YES' : 'NO';
};
